// src/pages/settings/notification-management-page/notification-management-column-model.tsx
import React, { useState, useEffect, useMemo } from 'react'
import { getColModelItem } from '../../../services/lib/utils/helpers'
import { urlKey } from '../../../services/api/api-urls'
import TextField from '@material-ui/core/TextField'
import Swal from 'sweetalert2'
import { Button, Tooltip } from 'antd'
import EditIcon from '@material-ui/icons/Edit'
import RecipientSelectorModal from './components/RecipientSelectorModal'
import RecipientDisplay from './components/RecipientDisplay'
import { ApiRequest } from '../../../services/api/api'

// Store modal state at module level to persist across re-renders
const modalState = {
  visible: false,
  openModal: null as ((config: any) => void) | null,
  closeModal: null as (() => void) | null,
  currentEmployeeIds: null as number[] | null,
  props: null as any
}

// Module-level cache for employees data
let cachedEmployees: Array<{ value: number; label: string }> = []
let employeesLoaded = false
let employeesLoading = false
const employeesListeners: Array<(employees: Array<{ value: number; label: string }>) => void> = []

// Load employees once and cache at module level
const loadEmployeesGlobal = async () => {
  if (employeesLoaded || employeesLoading) {
    return cachedEmployees
  }

  employeesLoading = true
  try {
    const result = await ApiRequest({
      otherUrl: 'Employee/GetAllEmployee',
      type: 'get',
    })
    const employees = result?.data?.data ?? result?.data ?? []
    cachedEmployees = (employees || []).map((emp: any) => {
      const name = `${emp.firstName ?? emp.FirstName ?? ''} ${emp.lastName ?? emp.LastName ?? ''}`.trim()
      return {
        value: emp.employeeId ?? emp.EmployeeId ?? 0,
        label: name || emp.userName || emp.UserName || `Employee ${emp.employeeId ?? emp.EmployeeId}`,
      }
    })
    employeesLoaded = true
    console.log('Employees loaded globally:', cachedEmployees.length)

    // Notify all listeners
    employeesListeners.forEach(listener => listener([...cachedEmployees]))
    employeesListeners.length = 0 // Clear listeners
  } catch (error) {
    console.error('Failed to load employees globally:', error)
  } finally {
    employeesLoading = false
  }

  return cachedEmployees
}

// Start loading employees immediately
loadEmployeesGlobal()

const NotificationManagementColumnModel = () => {
  const [allEmployees, setAllEmployees] = useState<Array<{ value: number; label: string }>>([])

  // Load all employees for display
  useEffect(() => {
    console.log('NotificationManagementColumnModel useEffect triggered')

    if (employeesLoaded) {
      console.log('Using cached employees:', cachedEmployees.length)
      setAllEmployees([...cachedEmployees])
    } else if (employeesLoading) {
      console.log('Adding listener for loading employees')
      employeesListeners.push((data) => {
        console.log('Listener called with employees:', data.length)
        setAllEmployees(data)
      })
    } else {
      console.log('Starting to load employees globally')
      loadEmployeesGlobal().then(data => {
        console.log('Employees loaded, setting state:', data.length)
        setAllEmployees([...data])
      })
    }
  }, [])

  const openRecipientModal = (props: any) => {
    console.log('openRecipientModal called', props)
    console.log('props.rowData:', props.rowData)
    console.log('props.value:', props.value)
    console.log('props.rowData?.employeeIds:', props.rowData?.employeeIds)
    console.log('props.rowData?.employeeNotifications:', props.rowData?.employeeNotifications)

    // Priority: use props.value (current value in edit mode) first
    let currentIds = props.value ?? props.rowData?.employeeIds ?? null

    // Only fallback to employeeNotifications if the above are null/undefined
    if (currentIds === null && props.rowData?.employeeNotifications && Array.isArray(props.rowData.employeeNotifications)) {
      currentIds = props.rowData.employeeNotifications.map((en: any) => en.employeeId)
      console.log('Extracted employeeIds from employeeNotifications (fallback):', currentIds)
    }

    console.log('Final currentIds to open modal:', currentIds)
    modalState.currentEmployeeIds = currentIds
    modalState.props = props
    modalState.visible = true
    // Force re-render by updating the component's state
    window.dispatchEvent(new CustomEvent('force-recipient-modal-open', { detail: { currentIds, props } }))
  }

  const handleRecipientConfirm = (employeeIds: number[]) => {
    console.log('handleRecipientConfirm', employeeIds)
    if (modalState.props) {
      if (modalState.props.onRowDataChange) {
        modalState.props.onRowDataChange({
          ...modalState.props.rowData,
          employeeIds: employeeIds
        })
      } else if (modalState.props.onChange) {
        modalState.props.onChange(employeeIds)
      }
    }
    modalState.visible = false
    window.dispatchEvent(new CustomEvent('force-recipient-modal-close'))
  }

  // RecipientSelectorModalWrapper component
  const RecipientSelectorModalWrapper = () => {
    const [visible, setVisible] = useState(false)
    const [initialIds, setInitialIds] = useState<number[] | null>(null)
    const [savedProps, setSavedProps] = useState<any>(null)

    useEffect(() => {
      const handleOpen = (e: any) => {
        console.log('Custom event received', e.detail)
        setInitialIds(e.detail.currentIds)
        setSavedProps(e.detail.props)
        setVisible(true)
      }

      const handleClose = () => {
        setVisible(false)
      }

      window.addEventListener('force-recipient-modal-open', handleOpen)
      window.addEventListener('force-recipient-modal-close', handleClose)

      return () => {
        window.removeEventListener('force-recipient-modal-open', handleOpen)
        window.removeEventListener('force-recipient-modal-close', handleClose)
      }
    }, [])

    const handleConfirm = (employeeIds: number[]) => {
      console.log('Modal confirm, saving employeeIds:', employeeIds, 'savedProps:', savedProps)
      if (savedProps) {
        if (savedProps.onRowDataChange) {
          console.log('Calling onRowDataChange with:', {
            ...savedProps.rowData,
            employeeIds: employeeIds
          })
          savedProps.onRowDataChange({
            ...savedProps.rowData,
            employeeIds: employeeIds
          })
        } else if (savedProps.onChange) {
          console.log('Calling onChange with:', employeeIds)
          savedProps.onChange(employeeIds)
        }
      } else {
        console.log('ERROR: savedProps is null!')
      }
      setVisible(false)
    }

    return (
      <RecipientSelectorModal
        visible={visible}
        onClose={() => setVisible(false)}
        onConfirm={handleConfirm}
        initialEmployeeIds={initialIds}
      />
    )
  }

  return useMemo(() => [
    {
      ...getColModelItem({
        title: 'Creator',
        field: 'creator',
        keywords: [urlKey.Employee]
      }, () => [{key: urlKey.Employee, label: '', otherOptions: {type: 'select', isOverrideSelectionOptions: true, valueJoinArray: ['firstName', 'lastName']}}]),
      editable: 'never', // 不允许修改创建者
    },
    // NEW: Recipients column
    {
      title: 'Recipients',
      field: 'employeeIds',
      editable: 'always',
      render: (rowData: any) => {
        console.log('render Recipients, rowData:', rowData)
        // Priority: rowData.employeeIds first, then fallback to employeeNotifications
        const ids = rowData.employeeIds ?? (rowData.employeeNotifications?.map((e: any) => e.employeeId)) ?? []
        console.log('render employeeIds:', ids)
        return (
          <RecipientDisplay
            employeeIds={ids}
            allEmployees={allEmployees}
            onEdit={() => {}}
            isEditing={false}
          />
        )
      },
      editComponent: (props: any) => {
        console.log('editComponent props', props, 'allEmployees:', allEmployees.length)
        // Try multiple possible data sources for edit mode
        const ids = props.value || (props.rowData?.employeeNotifications?.map((e: any) => e.employeeId)) || []
        return (
          <>
            <RecipientDisplay
              employeeIds={ids}
              allEmployees={allEmployees}
              onEdit={() => {
                console.log('Edit clicked, props:', props)
                openRecipientModal(props)
              }}
              isEditing={true}
            />
            <RecipientSelectorModalWrapper />
          </>
        )
      }
    },
    // Hidden column for sorting (newest first)
    {
      title: 'ID',
      field: 'notificationId',
      hidden: true,
      defaultSort: 'desc'
    },
    // ... rest of the existing columns (Start Date, End Date, Active, Content)
    {
      title: 'Start Date',
      align: 'left',
      field: 'startDate1',
      type: 'date',
      sorting: false
    },
    {
      title: 'End Date',
      align: 'left',
      field: 'endDate1',
      type: 'date',
      sorting: false
    },
    {
      title: 'Active',
      field: 'isActive1',
      lookup: {0: 'No', 1: 'Yes'},
      hidden: true
    },
    {
      title: 'Content',
      field: 'notificationContext',
      render: (rowData: any) => {
        const content = rowData.notificationContext || ''
        // Truncate content for display (max 100 chars)
        const truncated = content.length > 100 ? content.substring(0, 100) + '...' : content
        // Preserve whitespace and line breaks for display
        const displayContent = content || '-'

        return (
          <Tooltip
            title={
              <div style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxWidth: '500px',
                maxHeight: '300px',
                overflow: 'auto',
                fontSize: '13px',
                lineHeight: '1.5'
              }}>
                {content}
              </div>
            }
            placement="left"
            overlayStyle={{ maxWidth: '600px' }}
          >
            <div style={{
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '300px',
              maxHeight: '60px',
              lineHeight: '1.4',
              cursor: 'help'
            }}>
              {truncated}
            </div>
          </Tooltip>
        )
      },
      editComponent: (props: any) => {
        const handleOpenEditor = async () => {
          const { value: text } = await Swal.fire({
            title: 'Edit Content',
            input: 'textarea',
            inputLabel: 'Notification Content',
            inputValue: props.value || '',
            inputAttributes: {
              'aria-label': 'Type your notification content here',
              style: `
                min-height: 300px;
                height: 400px;
                font-size: 0.8em;
                line-height: 1;
                resize: vertical;
                width: 100%;
              `
            },
            customClass: {
              popup: 'swal2-popup-custom',
              input: 'swal2-textarea-custom',
              container: 'swal2-container-custom'
            },
            showCancelButton: true,
            confirmButtonText: 'Save',
            cancelButtonText: 'Cancel',
            width: '2000px',
            inputValidator: (value) => {
              if (!value || value.trim() === '') {
                return 'Content cannot be empty!'
              }
              return null
            }
          })

          if (text) {
            props.onChange(text)
          }
        }

        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <TextField
              value={props.value}
              fullWidth={true}
              multiline={true}
              minRows={2}
              onChange={e => props.onChange(e.target.value)}
            />
            <Button
              size="small"
              onClick={handleOpenEditor}
              style={{
                minWidth: '32px',
                height: '32px',
                padding: '0',
                borderRadius: '8px',
                backgroundColor: '#1890ff',
                color: '#fff',
                border: '1px solid #1890ff',
                boxShadow: '0 3px 8px rgba(24, 144, 255, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.25s ease-in-out',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#40a9ff'
                e.currentTarget.style.boxShadow = '0 4px 10px rgba(24,144,255,0.35)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1890ff'
                e.currentTarget.style.boxShadow = '0 3px 8px rgba(24,144,255,0.25)'
              }}
            >
              <EditIcon fontSize="small" style={{ fontSize: '16px' }} />
            </Button>
          </div>
        )
      }
    }
  ], [allEmployees])
}

export default NotificationManagementColumnModel
